import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns';
import { useProperty } from '@/lib/property/propertyContextDef';
import { useBookingsInRange, guestName, type BookingRow } from '@/lib/data/bookings';
import {
  buildMonthGrid,
  buildWeekGrid,
  groupByIsoDate,
  monthLabel,
  nextMonth,
  nextWeek,
  previousMonth,
  previousWeek,
  weekLabel,
} from '@/lib/calendar/grid';
import { ChevronBackIcon, ChevronForwardIcon } from '@/icons';
import { PageLoading } from '@/ui/Loader';
import { ButtonLink, Segmented } from '@/ui/primitives';
import { ListIcon } from '@/icons/nav';

export const Route = createFileRoute('/bookings/calendar')({
  component: BookingsCalendar,
});

const WEEK_OPTS = { weekStartsOn: 1 as const };

function BookingsCalendar() {
  const { businessId } = useProperty();
  const [view, setView] = useState<'month' | 'week'>('month');
  const [anchor, setAnchor] = useState(() => new Date());

  const rangeStart = view === 'month' ? startOfWeek(startOfMonth(anchor), WEEK_OPTS) : startOfWeek(anchor, WEEK_OPTS);
  const rangeEnd = view === 'month' ? endOfWeek(endOfMonth(anchor), WEEK_OPTS) : endOfWeek(anchor, WEEK_OPTS);
  const from = format(rangeStart, 'yyyy-MM-dd');
  const to = format(rangeEnd, 'yyyy-MM-dd');

  const { data: bookings, isLoading } = useBookingsInRange(businessId, from, to);

  const itemsByIso = useMemo(
    () => groupByIsoDate(bookings ?? [], (b) => b.check_in),
    [bookings],
  );

  const weeks = useMemo(
    () => (view === 'month' ? buildMonthGrid(anchor, itemsByIso) : [buildWeekGrid(anchor, itemsByIso)]),
    [view, anchor, itemsByIso],
  );

  return (
    <section className="flex flex-col gap-lg">
      <div className="flex flex-wrap items-center justify-between gap-md">
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Bookings — calendar</h1>
        <div className="flex items-center gap-sm">
          <Segmented
            ariaLabel="Calendar view"
            value={view}
            onChange={setView}
            options={[
              { value: 'month', label: 'Month' },
              { value: 'week', label: 'Week' },
            ]}
          />
          <ButtonLink to="/bookings" variant="secondary" size="sm" icon={<ListIcon className="h-4 w-4" aria-hidden="true" />}>
            List view
          </ButtonLink>
        </div>
      </div>

      <div className="flex items-center justify-between gap-md rounded-card border border-border bg-surface px-md py-sm shadow-card">
        <button
          type="button"
          onClick={() => setAnchor((d) => (view === 'month' ? previousMonth(d) : previousWeek(d)))}
          aria-label="Previous"
          className="rounded-button p-xs text-ink hover:bg-surface-2"
        >
          <ChevronBackIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <span className="text-body font-semibold text-ink">{view === 'month' ? monthLabel(anchor) : weekLabel(anchor)}</span>
        <button
          type="button"
          onClick={() => setAnchor((d) => (view === 'month' ? nextMonth(d) : nextWeek(d)))}
          aria-label="Next"
          className="rounded-button p-xs text-ink hover:bg-surface-2"
        >
          <ChevronForwardIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {isLoading ? <PageLoading /> : null}

      <div className="grid grid-cols-7 gap-xs">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="text-center text-caption font-semibold text-ink-2">
            {d}
          </div>
        ))}
        {weeks.flatMap((week) =>
          week.days.map((day) => (
            <div
              key={day.iso}
              className={`min-h-[96px] rounded-card border border-border p-xs ${
                day.inCurrentPeriod ? 'bg-surface' : 'bg-surface-2'
              } ${day.isToday ? 'ring-2 ring-canyon' : ''}`}
            >
              <p className={`text-caption ${day.inCurrentPeriod ? 'text-ink' : 'text-ink-3'}`}>{format(day.date, 'd')}</p>
              <ul className="mt-xs flex flex-col gap-xs">
                {day.items.slice(0, 3).map((b: BookingRow) => (
                  <li key={b.id}>
                    <Link
                      to="/bookings/$reference"
                      params={{ reference: b.reference_number ?? b.id }}
                      className="block truncate rounded-chip bg-tint-sand px-xs py-[1px] text-caption text-ink"
                      title={`${guestName(b)} — ${b.display_status ?? b.status}`}
                    >
                      {guestName(b)}
                    </Link>
                  </li>
                ))}
                {day.items.length > 3 ? (
                  <li className="text-caption text-ink-3">+{day.items.length - 3} more</li>
                ) : null}
              </ul>
            </div>
          )),
        )}
      </div>

      {bookings && bookings.length === 0 && !isLoading ? (
        <p className="text-body text-ink-2">No arrivals in this range.</p>
      ) : null}
    </section>
  );
}
